/**
 * VirtualizedList Component
 * 
 * A performant list component that uses virtualization to render only
 * visible items, enabling smooth scrolling with 1000+ items.
 * 
 * Uses @tanstack/react-virtual for virtualization.
 */

import { useRef, useCallback, useEffect } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { ReactNode } from 'react';
import type { ItemRenderProps } from './types';

// ============================================================================
// Types
// ============================================================================

export interface VirtualizedListProps<T> {
  /** Items to render */
  items: T[];
  
  /** Extract unique ID from item */
  getId: (item: T) => string;
  
  /** Render function for each item */
  renderItem: (item: T, props: ItemRenderProps) => ReactNode;
  
  /** Estimated row height (for initial layout) */
  estimatedRowHeight?: number;
  
  /** Whether rows have variable heights */
  variableHeights?: boolean;
  
  /** Selected item IDs */
  selectedIds: Set<string>;
  
  /** Toggle item selection */
  onToggle: (id: string) => void;
  
  /** Click handler for item */
  onItemClick?: (item: T) => void;
  
  /** Double-click handler */
  onItemDoubleClick?: (item: T) => void;
  
  /** Whether selection is enabled */
  selectable?: boolean;
  
  /** Keyboard navigation enabled */
  enableKeyboardNavigation?: boolean;
  
  /** Currently focused item ID (for keyboard nav) */
  focusedId?: string | null;
  
  /** Callback when focus changes via keyboard */
  onFocusChange?: (id: string | null) => void;
  
  /** Container height (defaults to 100%) */
  height?: number | string;
  
  /** Additional className for container */
  className?: string;
  
  /** Gap between items */
  gap?: number;
  
  /** Overscan count (items to render outside viewport) */
  overscan?: number;
}

// ============================================================================
// Keyboard Navigation Hook
// ============================================================================

function useKeyboardNavigation<T>(
  items: T[],
  getId: (item: T) => string,
  focusedId: string | null | undefined,
  onFocusChange: ((id: string | null) => void) | undefined,
  onItemClick: ((item: T) => void) | undefined,
  enabled: boolean
) {
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (!enabled || !onFocusChange) return;
    
    const currentIndex = focusedId 
      ? items.findIndex(item => getId(item) === focusedId)
      : -1;
    
    switch (e.key) {
      case 'j':
      case 'ArrowDown': {
        e.preventDefault();
        const nextIndex = Math.min(currentIndex + 1, items.length - 1);
        if (nextIndex >= 0 && items[nextIndex]) {
          onFocusChange(getId(items[nextIndex]));
        }
        break;
      }
      case 'k':
      case 'ArrowUp': {
        e.preventDefault();
        const prevIndex = Math.max(currentIndex - 1, 0);
        if (prevIndex >= 0 && items[prevIndex]) {
          onFocusChange(getId(items[prevIndex]));
        }
        break;
      }
      case 'Enter':
      case 'o': {
        if (focusedId && onItemClick) {
          e.preventDefault();
          const item = items.find(i => getId(i) === focusedId);
          if (item) {
            onItemClick(item);
          }
        }
        break;
      }
      case 'Home': {
        e.preventDefault();
        if (items.length > 0) {
          onFocusChange(getId(items[0]));
        }
        break;
      }
      case 'End': {
        e.preventDefault();
        if (items.length > 0) {
          onFocusChange(getId(items[items.length - 1]));
        }
        break;
      }
    }
  }, [enabled, items, getId, focusedId, onFocusChange, onItemClick]);
  
  useEffect(() => {
    if (enabled) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [enabled, handleKeyDown]);
}

// ============================================================================
// VirtualizedList Component
// ============================================================================

export function VirtualizedList<T>({
  items,
  getId,
  renderItem,
  estimatedRowHeight = 72,
  variableHeights = false,
  selectedIds,
  onToggle,
  onItemClick,
  onItemDoubleClick,
  selectable = false,
  enableKeyboardNavigation = true,
  focusedId,
  onFocusChange,
  height = '100%',
  className = '',
  gap = 8,
  overscan = 5,
}: VirtualizedListProps<T>) {
  const parentRef = useRef<HTMLDivElement>(null);
  
  // Set up keyboard navigation
  useKeyboardNavigation(
    items,
    getId,
    focusedId,
    onFocusChange,
    onItemClick,
    enableKeyboardNavigation
  );
  
  // Set up virtualizer
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimatedRowHeight,
    overscan,
    gap,
  });
  
  const virtualItems = virtualizer.getVirtualItems();
  
  // Scroll focused item into view
  useEffect(() => {
    if (focusedId) {
      const index = items.findIndex(item => getId(item) === focusedId);
      if (index >= 0) {
        virtualizer.scrollToIndex(index, { align: 'auto' });
      }
    }
  }, [focusedId, items, getId, virtualizer]);
  
  // Handle item click
  const handleItemClick = useCallback((item: T, e: React.MouseEvent) => {
    const id = getId(item);
    
    // Update focus
    onFocusChange?.(id);
    
    // Handle selection
    if (selectable && e.ctrlKey) {
      onToggle(id);
    } else if (onItemClick) {
      onItemClick(item);
    }
  }, [getId, selectable, onToggle, onItemClick, onFocusChange]);
  
  // Handle double click
  const handleItemDoubleClick = useCallback((item: T) => {
    onItemDoubleClick?.(item);
  }, [onItemDoubleClick]);
  
  return (
    <div
      ref={parentRef}
      className={`overflow-auto ${className}`}
      style={{ height }}
      role="listbox"
      aria-label="Item list"
      tabIndex={0}
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        {virtualItems.map((virtualItem) => {
          const item = items[virtualItem.index];
          const id = getId(item);
          const isSelected = selectedIds.has(id);
          const isFocused = focusedId === id;
          
          const itemProps: ItemRenderProps = {
            selected: isSelected,
            onSelect: () => onToggle(id),
            onToggle: () => onToggle(id),
            highlighted: isFocused,
          };
          
          return (
            <div
              key={virtualItem.key}
              data-index={virtualItem.index}
              ref={variableHeights ? virtualizer.measureElement : undefined}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                transform: `translateY(${virtualItem.start}px)`,
              }}
              role="option"
              aria-selected={isSelected}
              onClick={(e) => handleItemClick(item, e)}
              onDoubleClick={() => handleItemDoubleClick(item)}
              className={`
                cursor-pointer  transition-all
                ${isFocused ? 'ring-2 ring-primary-500 ring-offset-1' : ''}
                ${isSelected ? 'bg-primary-50' : 'hover:bg-[var(--surface)]'}
              `}
            >
              <div className="p-3 border border-[var(--rule)] bg-[var(--bg-raised)]">
                {renderItem(item, itemProps)}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default VirtualizedList;
